import { useRef, useEffect, useState } from 'react';
import { parseSkipTranslationTags } from '../../../../services/util';
import { getUserData } from '../../../../services/API';

const useIdleTimer = (speech, idleEncouragementTimeout, idleExpiredTimeout, speakEncouragement) => {
  const currentIdleTimeout = useRef(undefined);
  const [idleSpeechPlaying, setIdleSpeechPlaying] = useState(undefined); // Instantiate this value as undefined so useEffects can tell difference between undefined and false
  const [idleTimeoutExpired, setIdleTimeoutExpired] = useState(false);

  const clearIdleTimer = () => {
    if (currentIdleTimeout.current) clearTimeout(currentIdleTimeout.current);
  };

  const resetIdleTimer = (idleEncouragementText) => {
    clearIdleTimer();
    startIdleTimer(idleEncouragementText);
  };

  const startIdleTimer = (idleEncouragementText) => {
    setIdleTimeoutExpired(false);
    currentIdleTimeout.current = setTimeout(async () => {
      if (idleEncouragementText) {
        setIdleSpeechPlaying(true);

        // A rejection from any of these speech calls must not strand idleSpeechPlaying
        // (finally clears it) nor skip arming the expired timer below (catch keeps the
        // async callback from unwinding), so the idle → auto-submit flow still advances.
        try {
          // Caller-provided playback takes precedence (Evaluar syllable blending plays
          // each syllable of the nudge from pre-recorded mp3 instead of TTS-ing the raw
          // "ce, ro" list). Otherwise handle skip-translation tags as Phrase.js does.
          if (speakEncouragement) {
            await speakEncouragement(idleEncouragementText);
          } else if(getUserData().bilingualMode){
            // Get "skip translation" parsed instructions
            let translationParsedInstructions = parseSkipTranslationTags(idleEncouragementText);

            if (translationParsedInstructions.length > 1) {
              // We have skipTranslation tags, use speakSequence
              const bilingualSequenceToSpeak = {
                textToSpeak: translationParsedInstructions,
                passThroughSpeechTags: true,
              }
              await speech.speakSequence(bilingualSequenceToSpeak);
            } else {
              // No skip translation tags, use regular speak
              await speech.speakWithPromise({
                textToSpeak: idleEncouragementText,
              });
            }
          } else {
            // Non-bilingual mode, use regular speak
            await speech.speakWithPromise({
              textToSpeak: idleEncouragementText,
            });
          }
        } catch (err) {
          console.error('Idle encouragement playback failed:', err);
        } finally {
          setIdleSpeechPlaying(false);
        }
      }
      currentIdleTimeout.current = setTimeout(() => {
        setIdleTimeoutExpired(true);
      }, idleExpiredTimeout);
    }, idleEncouragementTimeout);
  };

  useEffect(() => {
    return () => {
      clearIdleTimer();
    };
  }, []);

  return {
    resetIdleTimer,
    clearIdleTimer,
    idleSpeechPlaying,
    idleTimeoutExpired
  };
};

export default useIdleTimer;
